/**
 * DrawingDoneCheckboxCell Component
 * Checkbox component for marking orders as drawing done in the drawing workflow
 */

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useUpdateOrderMutation } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import type { OrderSummary } from '../../utils/types';

interface DrawingDoneCheckboxCellProps {
  order: OrderSummary;
  className?: string;
  onDoneChange?: (orderId: string, done: boolean) => void;
}

export const DrawingDoneCheckboxCell: React.FC<DrawingDoneCheckboxCellProps> = ({ 
  order, 
  className = '',
  onDoneChange 
}) => {
  const [updateOrder, { isLoading }] = useUpdateOrderMutation();
  const toast = useToast();
  
  // Get current drawing done status from metadata
  const currentDone = order.metadata?.drawing_workflow?.done ?? false;
  const [isUpdating, setIsUpdating] = useState(false);

  const handleDoneToggle = async () => {
    if (isLoading || isUpdating) return;
    
    setIsUpdating(true);
    const newDoneStatus = !currentDone;
    
    try {
      // Update the metadata with drawing workflow status
      const updatedMetadata = {
        ...order.metadata,
        drawing_workflow: {
          ...order.metadata?.drawing_workflow,
          done: newDoneStatus,
          done_at: newDoneStatus ? new Date().toISOString() : null,
          done_by: newDoneStatus ? 'current_user' : null // This would be replaced with actual user info
        }
      };
      
      await updateOrder({ 
        id: order.id, 
        data: { metadata: updatedMetadata }
      }).unwrap();
      
      toast.success(
        newDoneStatus ? 'Drawing Done' : 'Drawing Done Unchecked', 
        newDoneStatus 
          ? 'Order marked as drawing done - workflow options now available' 
          : 'Drawing done status removed'
      );
      
      // Notify parent component of the change
      onDoneChange?.(order.id, newDoneStatus);
      
    } catch (e: any) {
      toast.error('Update failed', e?.data?.detail || 'Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const cellClasses = [
    'p-2 border',
    'text-center',
    className
  ].filter(Boolean).join(' ');

  return (
    <td className={cellClasses}>
      <div className="flex items-center justify-center">
        <div className="flex items-center">
          <label className="relative cursor-pointer group">
            <input
              type="checkbox"
              checked={currentDone}
              onChange={handleDoneToggle}
              disabled={isLoading || isUpdating}
              className="sr-only"
            />
            <div 
              className={`
                w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200
                ${currentDone 
                  ? 'bg-purple-500 border-purple-500' 
                  : 'border-gray-300 hover:border-purple-400 group-hover:bg-purple-50'
                }
                ${(isLoading || isUpdating) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {currentDone && (
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              )}
            </div>
          </label>
          {(isLoading || isUpdating) && (
            <div className="ml-2 w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin"></div>
          )}
        </div>
      </div>
    </td>
  );
};

DrawingDoneCheckboxCell.displayName = 'DrawingDoneCheckboxCell';
